import React, { useContext, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { AsYouType } from 'libphonenumber-js/max'
import FormControl from 'react-bootstrap/lib/FormControl'
import PhoneContext from './PhoneContext'
import PhoneErrors from './PhoneErrors'

const PHONE_PLACEHOLDERS_BY_COUNTRY = {
  AU: '0400 000 000',
  NZ: '021 123 4567',
  US: '(201) 555-0123',
  CA: '(204) 234-5678',
  GB: '07123 456789'
}

const territoryPhonePlaceholder = countryCode => {
  const country = (countryCode || 'AU').toUpperCase()
  return PHONE_PLACEHOLDERS_BY_COUNTRY[country] || 'Mobile phone number'
}

const RegisterPhoneInput = ({ validations, onChange, placeholder, Wrapper, countryCode, showInlineErrors = true }) => {
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState('')
  const [phonePlaceholder, setPhonePlaceholder] = useState(placeholder || territoryPhonePlaceholder(countryCode))
  const [phoneFormatValid, setPhoneFormatValid] = useState(null)
  const [isCheckingPhoneUnique, setIsCheckingPhoneUnique] = useState(false)

  const phoneInputRef = useRef(null)
  const itiRef = useRef(null)

  const {
    phoneNumber,
    phoneUnique,
    setPhoneNumber,
    setPhoneUnique,
    setErrorMessage
  } = useContext(PhoneContext)

  const selectedCountry = () => itiRef.current?.getSelectedCountryData?.() || {}

  const currentPhonePlaceholder = () => {
    if (placeholder) return placeholder

    const country = selectedCountry().iso2 || countryCode
    const utils = window.intlTelInputUtils
    if (country && utils?.getExampleNumber) {
      const example = utils.getExampleNumber(country.toLowerCase(), true, utils.numberType?.MOBILE)
      if (example) return example
    }

    return territoryPhonePlaceholder(country)
  }

  const numberForFormatting = value => {
    const dialCode = selectedCountry().dialCode
    const digits = (value || '').replace(/\D/g, '')
    if (!digits) return value

    if (dialCode && value.trim().startsWith('+') && digits.startsWith(dialCode)) {
      const localDigits = digits.slice(dialCode.length).replace(/^0/, '')
      return `+${dialCode}${localDigits}`
    }

    if (dialCode && digits.startsWith('0')) {
      return `+${dialCode}${digits.replace(/^0/, '')}`
    }

    return value
  }

  const localDisplayNumber = value => {
    const dialCode = selectedCountry().dialCode
    let digits = (value || '').replace(/\D/g, '')

    if (dialCode && digits.startsWith(dialCode)) {
      digits = digits.slice(dialCode.length)
    }

    if ((countryCode || 'AU').toUpperCase() === 'AU' && digits.startsWith('4') && digits.length === 9) {
      digits = `0${digits}`
    }

    if ((countryCode || 'AU').toUpperCase() === 'AU' && digits.startsWith('04') && digits.length >= 10) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
    }

    return (value || '').replace(/^\+\d+\s*/, '').replace(/^\+/, '')
  }

  const formatDisplayNumber = value => {
    if (!value) return ''

    const normalizedNumber = numberForFormatting(value)
    if (itiRef.current?.setNumber && normalizedNumber) {
      itiRef.current.setNumber(normalizedNumber)
    }

    const utils = window.intlTelInputUtils
    const country = selectedCountry().iso2?.toUpperCase()
    if (utils?.formatNumber && utils?.numberFormat?.NATIONAL && normalizedNumber && country) {
      const formatted = utils.formatNumber(normalizedNumber, country, utils.numberFormat.NATIONAL)
      if (formatted) return formatted
    }

    return localDisplayNumber(value)
  }

  const formatDisplayNumberAsYouType = value => {
    if (!value) return ''

    const asYouType = new AsYouType(countryCode ? countryCode.toUpperCase() : 'AU')
    return asYouType.input(value)
  }

  const validPhoneFormat = value => {
    if (!value) return null

    const asYouType = new AsYouType(countryCode ? countryCode.toUpperCase() : 'AU')
    asYouType.input(value)
    return asYouType.isValid()
  }

  const normalizedPhoneNumber = value => {
    if (!value) return ''

    const formattedInput = numberForFormatting(value) || value
    if (itiRef.current?.setNumber) {
      itiRef.current.setNumber(formattedInput)
    }

    if (itiRef.current?.getNumber) {
      const number = itiRef.current.getNumber()
      if (number) return number
    }

    return formattedInput
  }

  const clearPhoneNumber = id => {
    if (itiRef.current?.setNumber) {
      itiRef.current.setNumber('')
    }

    if (phoneInputRef.current) {
      phoneInputRef.current.value = ''
    }

    setDisplayPhoneNumber('')
    setPhoneNumber('')
    setPhoneFormatValid(null)
    setPhoneUnique(null)
    setIsCheckingPhoneUnique(false)
    onChange && id && onChange({ target: { value: '', id } })
  }

  useEffect(() => {
    if (!phoneInputRef.current || !window.intlTelInput) return undefined

    itiRef.current = window.intlTelInput(phoneInputRef.current, {
      initialCountry: countryCode ? countryCode.toLowerCase() : 'au',
      utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@23.0.4/build/js/utils.js',
      separateDialCode: true,
      nationalMode: true,
      formatOnDisplay: true,
      autoPlaceholder: 'off'
    })

    const syncCountryChange = () => {
      const formatted = formatDisplayNumber(phoneInputRef.current.value)
      const normalized = normalizedPhoneNumber(formatted)
      setPhonePlaceholder(currentPhonePlaceholder())
      setDisplayPhoneNumber(formatted)
      setPhoneNumber(normalized)
      setPhoneFormatValid(validPhoneFormat(formatted))
      onChange && onChange({ target: { value: normalized, id: phoneInputRef.current.id || 'phone' } })
    }

    const inputElement = phoneInputRef.current
    inputElement.addEventListener('countrychange', syncCountryChange)
    setPhonePlaceholder(currentPhonePlaceholder())

    return () => {
      inputElement.removeEventListener('countrychange', syncCountryChange)
      if (itiRef.current) {
        itiRef.current.destroy()
        itiRef.current = null
      }
    }
  }, [countryCode])

  useEffect(() => {
    if (placeholder || !itiRef.current) {
      setPhonePlaceholder(placeholder || territoryPhonePlaceholder(countryCode))
    }
  }, [placeholder, countryCode])

  useEffect(() => {
    let cancelled = false
    setPhoneUnique(null)
    setIsCheckingPhoneUnique(false)

    if (!phoneNumber) {
      setPhoneFormatValid(null)
      return undefined
    }

    const nextPhoneFormatValid = validPhoneFormat(phoneNumber)
    setPhoneFormatValid(nextPhoneFormatValid)
    if (!nextPhoneFormatValid || validations === false) return undefined
    setIsCheckingPhoneUnique(true)

    const checkPhoneUnique = async () => {
      try {
        const response = await fetch(`/phone_numbers/check_unique?phone=${encodeURIComponent(phoneNumber)}`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          }
        })
        if (!response.ok) throw new Error('Network response was not ok')
        const data = await response.json()
        if (cancelled) return
        setPhoneUnique(data.is_unique)
        setErrorMessage(null)
      } catch (e) {
        if (cancelled) return
        setPhoneUnique(false)
        setErrorMessage(<FormattedMessage id="client.phone_verification.errors.check_failed" defaultMessage="Failed to check phone number uniqueness." />)
      } finally {
        if (!cancelled) setIsCheckingPhoneUnique(false)
      }
    }

    checkPhoneUnique()
    return () => { cancelled = true }
  }, [phoneNumber, validations, countryCode])

  const valid = (phoneFormatValid === false || validations === false)
    ? false
    : (phoneUnique === null ? null : !!phoneUnique && (typeof validations === 'boolean' ? validations : true))

  const handleChange = e => {
    const displayNumber = formatDisplayNumberAsYouType(e.target.value)

    if (!displayNumber) {
      clearPhoneNumber(e.target.id)
      setErrorMessage(null)
      return
    }

    const normalized = normalizedPhoneNumber(displayNumber)
    setDisplayPhoneNumber(displayNumber)
    setPhoneNumber(normalized)
    setPhoneFormatValid(validPhoneFormat(displayNumber))
    setPhoneUnique(null)
    setErrorMessage(null)

    onChange && onChange({ target: { value: normalized, id: e.target.id } })
  }

  const handleBlur = () => {
    const currentValue = phoneInputRef.current?.value || displayPhoneNumber

    if (!currentValue) {
      clearPhoneNumber(phoneInputRef.current?.id || 'phone')
      return
    }

    const formatted = formatDisplayNumber(currentValue)
    const normalized = normalizedPhoneNumber(formatted)
    setDisplayPhoneNumber(formatted)
    setPhoneNumber(normalized)
    setPhoneFormatValid(validPhoneFormat(formatted))

    onChange && onChange({ target: { value: normalized, id: phoneInputRef.current?.id || 'phone' } })
  }

  const input = (
    <>
      <div className="iti-react-wrapper">
        <FormControl
          id="phone"
          inputRef={(el) => { phoneInputRef.current = el }}
          placeholder={phonePlaceholder}
          type="text"
          componentClass="input"
          value={displayPhoneNumber}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
      {showInlineErrors && <PhoneErrors validations={validations} displayType="alert" />}
    </>
  )

  if (Wrapper) {
    return (
      <Wrapper valid={valid} validating={isCheckingPhoneUnique} name="phone">
        {input}
      </Wrapper>
    )
  }

  return input
}

export default RegisterPhoneInput
